import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUp,
  ArrowUpRight,
  ChevronDown,
  Mail,
  MapPin,
  Clock,
} from "lucide-react";
import { companyInfo, services, formations, produitsLizpack } from "../data/mockData";
import { Logo } from "./Logo";

const produits = [
  ...produitsLizpack.map((p) => ({ label: p.nom, href: p.lien })),
  { label: "QGIS MCP", href: "https://qgismcp.geodonnee.com/" },
];

const { contact } = companyInfo;

const coordonnees = [
  { icon: Mail, label: contact.email, href: `mailto:${contact.email}` },
  {
    icon: MapPin,
    label: `${contact.street}, ${contact.city}, ${contact.region} ${contact.postalCode}`,
  },
  { icon: Clock, label: "Lun – ven, 9h – 17h (HNE)" },
];

const titreColonne =
  "text-vt-legende font-extrabold uppercase tracking-[0.16em] text-white/80";
const lienPied =
  "text-vt-controle font-semibold text-white/80 transition-colors hover:text-white";

const BUREAU = "(min-width: 1024px)";

/** Vrai sur grand écran, où les colonnes restent toutes ouvertes. */
const useBureau = () => {
  const [bureau, setBureau] = useState(false);
  useEffect(() => {
    const requete = window.matchMedia(BUREAU);
    const maj = () => setBureau(requete.matches);
    maj();
    requete.addEventListener("change", maj);
    return () => requete.removeEventListener("change", maj);
  }, []);
  return bureau;
};

/**
 * Colonne du pied de page : un titre sur grand écran, un accordéon sur
 * téléphone et tablette. Fermée, la liste reste visible sur grand écran
 * (`lg:block`) même avant que JavaScript ne s'exécute.
 */
const Colonne = ({ id, titre, bureau, enNav = true, children }) => {
  const [ouvert, setOuvert] = useState(false);
  const Balise = enNav ? "nav" : "div";
  return (
    <Balise
      aria-labelledby={enNav ? id : undefined}
      className="border-b border-white/20 lg:border-0"
    >
      <h2 id={id} className={titreColonne}>
        {bureau ? (
          titre
        ) : (
          <button
            type="button"
            onClick={() => setOuvert((o) => !o)}
            aria-expanded={ouvert}
            aria-controls={`${id}-liste`}
            className="flex w-full items-center justify-between py-4 text-left uppercase lg:py-0"
          >
            {titre}
            <ChevronDown
              className={`h-5 w-5 flex-none lg:hidden ${ouvert ? "rotate-180" : ""}`}
              strokeWidth={2.4}
              aria-hidden="true"
            />
          </button>
        )}
      </h2>
      <div
        id={`${id}-liste`}
        className={`${ouvert || bureau ? "block" : "hidden"} pb-5 lg:block lg:pb-0 lg:pt-5`}
      >
        {children}
      </div>
    </Balise>
  );
};

export const Footer = () => {
  const bureau = useBureau();
  return (
    <footer className="on-dark bg-brand px-6 pb-8 pt-12 sm:px-10 lg:px-12 lg:pt-14">
      <div className="shell">
        <div className="grid lg:grid-cols-5 lg:gap-10 lg:border-b lg:border-white/20 lg:pb-10">
          <div className="pb-6 lg:pb-0">
            <Logo clair />
            <p className="mt-5 max-w-[38ch] text-vt-controle leading-relaxed text-white/80">
              Traitement, automatisation et diffusion de données géospatiales.
              Formation d'équipes techniques. Gatineau, Québec.
            </p>
          </div>

          <div className="border-t border-white/20 lg:contents">
            <Colonne id="pied-produits" titre="Produits" bureau={bureau}>
              <ul className="space-y-3">
                {produits.map((item) => (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center gap-1 ${lienPied}`}
                    >
                      {item.label}
                      <ArrowUpRight className="h-4 w-4" strokeWidth={2.2} aria-hidden="true" />
                      <span className="sr-only">(nouvel onglet)</span>
                    </a>
                  </li>
                ))}
              </ul>
            </Colonne>

            <Colonne id="pied-services" titre="Services" bureau={bureau}>
              <ul className="space-y-3">
                {services.map((s) => (
                  <li key={s.slug}>
                    <Link to={`/services/${s.slug}`} className={lienPied}>
                      {s.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </Colonne>

            <Colonne id="pied-formations" titre="Formations" bureau={bureau}>
              <ul className="space-y-3">
                {formations.map((f) => (
                  <li key={f.slug}>
                    <Link to={`/formations/${f.slug}`} className={lienPied}>
                      {f.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </Colonne>

            <Colonne id="pied-contact" titre="Nous joindre" bureau={bureau} enNav={false}>
              <ul className="space-y-4">
                {coordonnees.map(({ icon: Icon, label, href }) => (
                  <li
                    key={label}
                    className="flex items-start gap-3 text-vt-controle font-semibold text-white/80"
                  >
                    <Icon
                      className="mt-0.5 h-[1.125rem] w-[1.125rem] flex-none"
                      strokeWidth={2.2}
                      aria-hidden="true"
                    />
                    {href ? (
                      <a href={href} className="transition-colors hover:text-white">
                        {label}
                      </a>
                    ) : (
                      <span>{label}</span>
                    )}
                  </li>
                ))}
              </ul>
            </Colonne>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 pt-7 sm:flex-row">
          <p className="text-vt-controle font-semibold text-white/80">
            © {new Date().getFullYear()} Geodonnée. Tous droits réservés.
          </p>
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0 })}
            aria-label="Haut de page"
            title="Haut de page"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/60 text-white"
          >
            <ArrowUp className="h-5 w-5" strokeWidth={2.4} aria-hidden="true" />
          </button>
        </div>
      </div>
    </footer>
  );
};
